"use client";

import { ArrowRight, ShoppingBag, Sparkles, Tag } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { formatPrice } from "@/lib/utils";
import type { Product } from "@/types";

export function WebsiteExclusiveDiscounts({ products }: { products: Product[] }) {

  // Filter real products from catalog that have actual discounts (no fabricated numbers)
  const realDeals = products
    .filter((p) => (p.mrp && p.mrp > p.price) || p.discount > 0)
    .sort((a, b) => {
      const discA = a.mrp && a.mrp > a.price ? (a.mrp - a.price) / a.mrp : a.discount / 100;
      const discB = b.mrp && b.mrp > b.price ? (b.mrp - b.price) / b.mrp : b.discount / 100;
      return discB - discA;
    })
    .slice(0, 6);

  return (
    <section className="mx-auto max-w-[1500px] px-3 sm:px-4">
      <div className="overflow-hidden rounded-2xl border-2 border-[#00B7B0]/40 bg-gradient-to-br from-[#073B70] via-[#06305C] to-[#041D38] p-4 sm:p-6 text-white shadow-lg">
        {/* Top Header Banner matching the official sticker promotion */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-white/10 pb-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FF8A00] px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-sm">
              <Tag className="h-3.5 w-3.5" />
              <span>ORDER ONLINE &amp; GET</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white">
              EXCLUSIVE DISCOUNTS
            </h2>
            <p className="text-xs sm:text-sm font-semibold text-[#00B7B0]">
              Better Deals When You Shop on Our Website &bull;{" "}
              <span className="text-slate-200">More Value, Same Great Products!</span>
            </p>
          </div>

          <Link
            href="/search?sort=discount"
            className="inline-flex items-center gap-2 rounded-xl bg-[#FF8A00] px-5 py-3 text-sm font-bold text-white shadow-md transition hover:bg-[#E67C00] active:scale-95 shrink-0"
          >
            <span>Shop All Deals</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Real Product Showcase */}
        {realDeals.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 sm:gap-4">
            {realDeals.map((product) => {
              const effectiveMrp = product.mrp > product.price ? product.mrp : product.price;
              const savings = effectiveMrp - product.price;
              const discountPercent =
                effectiveMrp > product.price
                  ? Math.round(((effectiveMrp - product.price) / effectiveMrp) * 100)
                  : product.discount;

              return (
                <div
                  key={product.id}
                  className="group flex flex-col justify-between rounded-xl bg-white p-2.5 sm:p-3 text-[#172B3A] shadow-sm transition hover:shadow-lg dark:bg-slate-900 dark:text-white"
                >
                  <Link
                    href={`/products/${product.slug}`}
                    className="relative block aspect-square w-full overflow-hidden rounded-lg bg-[#F5F7FA] dark:bg-slate-800"
                  >
                    {product.images?.[0] ? (
                      <Image
                        src={product.images[0]}
                        alt={product.title}
                        fill
                        sizes="200px"
                        className="object-contain p-2 transition duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <ShoppingBag className="h-8 w-8 text-slate-400 stroke-[1.25]" />
                      </div>
                    )}

                    {discountPercent > 0 && (
                      <span className="absolute left-1.5 top-1.5 rounded bg-[#FF8A00] px-1.5 py-0.5 text-[10px] sm:text-xs font-bold text-white shadow-2xs">
                        -{discountPercent}%
                      </span>
                    )}
                  </Link>

                  <Link href={`/products/${product.slug}`} className="mt-2.5 flex flex-1 flex-col justify-between block group/item">
                    <div>
                      <h3
                        className="line-clamp-2 text-xs font-semibold leading-snug text-[#073B70] group-hover/item:text-[#087DB5] dark:text-white dark:group-hover/item:text-[#00B7B0] transition-colors"
                        title={product.title}
                      >
                        {product.title}
                      </h3>

                      <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
                        <span className="text-sm sm:text-base font-bold text-[#073B70] dark:text-white">
                          {formatPrice(product.price)}
                        </span>
                        {savings > 0 && (
                          <span className="text-[10px] sm:text-xs text-slate-400 line-through">
                            {formatPrice(effectiveMrp)}
                          </span>
                        )}
                      </div>

                      {savings > 0 && (
                        <p className="mt-0.5 text-[10px] font-bold text-[#00B7B0]">
                          Save {formatPrice(savings)}
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-5 rounded-xl bg-white/10 p-6 text-center text-slate-200">
            <Sparkles className="mx-auto h-8 w-8 text-[#00B7B0]" />
            <p className="mt-2 text-sm font-semibold">Special website promotional offers updated daily.</p>
            <Link
              href="/search"
              className="mt-3 inline-block rounded-lg bg-[#FF8A00] px-4 py-2 text-xs font-bold text-white hover:bg-[#E67C00]"
            >
              Browse Catalog
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
